import { checkFeatureGate } from './feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { isIsolatedCloud as isIsolatedCloudFromContext } from '@atlaskit/atlassian-context';

/**
 * @deprecated Use `isIsolatedCloud` from `@atlaskit/atlassian-context` directly instead.
 *
 * @note !!!! no references to build-time evaluated values should be used, as it is no longer possible to deduce whether
 * or not an environment is IC or not from just the hostname - we are required to use the Atl-Ctx-* cookies to determine this.
 *
 * This means that you _must_ not use export or use any static values to determine anything that includes IC specific logic.
 */
export type Env =
  | 'local'
  | 'dev'
  | 'stg'
  | 'stg-fedm'
  | 'prod'
  | 'prod-fedm'
  | 'prod-ic'
  | 'unknown';

export type EnvType = 'dev' | 'staging' | 'prod' | 'unknown';

export type EnvConfig = {
  recaptcha: {
    smsEnrollmentRecaptchaEnterpriseInvisibleKey: string;
    smsEnrollmentRecaptchaEnterpriseNewKey: string;
  };
};

/**
 * Used to infer the environment based on the hostname - with the exception of IC environments which use Alt-Ctx-* cookies
 * This is abstracted away by the `@atlassian/atlassian-context` library.
 *
 * Outside of the IC case, the hostname is used to determine the environment type.
 */
function inferEnv(hostname: string): Env {
  if (hostname === 'localhost') {
    return 'local';
  }

  // Isolated Cloud is identified via the Atl-Ctx-* cookies (through the `atlassian-context` library),
  // not the hostname - only after we check the cookies, can we determine whether or not the environment is IC. If it is,
  // we return 'prod-ic' as the env, since all IC environments are being treated as production.
  // Only once that's checked, can we fallback to checking the hostname for non-IC environments
  if (isIsolatedCloudFromContext()) {
    return 'prod-ic';
  }

  if (hostname.endsWith('.dev.internal.atlassian.com') || hostname.endsWith('.dev.atlassian.com')) {
    return 'dev';
  }

  if (hostname.endsWith('.stg.internal.atlassian.com') || hostname.endsWith('.stg.atlassian.com')) {
    return 'stg';
  }

  if (hostname.endsWith('.stg.atlassian-us-gov-mod.com')) {
    return 'stg-fedm';
  }

  if (hostname.endsWith('.atlassian-us-gov-mod.com')) {
    return 'prod-fedm';
  }

  if (hostname.endsWith('.atlassian.com')) {
    return 'prod';
  }

  return 'unknown';
}

export const getCurrentEnv = (): Env => inferEnv(window.location.hostname);

const envTypes: Record<Env, EnvType> = {
  local: 'dev',
  dev: 'dev',
  stg: 'staging',
  'stg-fedm': 'staging',
  prod: 'prod',
  'prod-fedm': 'prod',
  'prod-ic': 'prod',
  unknown: 'unknown',
};

export const getCurrentEnvType = (): EnvType => envTypes[getCurrentEnv()];

// Origins for environments with a fixed, known IDAC host. The `local` and `prod-ic` origins
// depend on `window.location` and so must be computed at call time (see `getIdacOrigin`).
const idacOrigins: Record<Exclude<Env, 'local' | 'prod-ic'>, string> = {
  dev: 'https://id.dev.internal.atlassian.com',
  stg: 'https://id.stg.internal.atlassian.com',
  'stg-fedm': 'https://id.stg.atlassian-us-gov-mod.com',
  prod: 'https://id.atlassian.com',
  'prod-fedm': 'https://id.atlassian-us-gov-mod.com',
  unknown: 'https://id.atlassian.com', // safe default
};

export const getIdacOrigin = (): string => {
  const env = getCurrentEnv();

  switch (env) {
    // The local proxy is created within Parcel on the frontend local step, and requests are proxied
    // to staging. Using `window.location.protocol` means we don't need a change to run over HTTP/HTTPS.
    case 'local':
      return `${window.location.protocol}//localhost:3000`; // idproxy local
    // Dynamic, based on the individual IC instance.
    case 'prod-ic':
      return window.location.origin;
    default:
      return idacOrigins[env];
  }
};

export type AtlassianCloudType = 'commercial' | 'fedramp-moderate' | 'isolated-cloud';

const atlassianCloudType: Record<Env, AtlassianCloudType> = {
  local: 'commercial',
  dev: 'commercial',
  stg: 'commercial',
  'stg-fedm': 'fedramp-moderate',
  prod: 'commercial',
  'prod-fedm': 'fedramp-moderate',
  'prod-ic': 'isolated-cloud',
  unknown: 'commercial',
};

export type Perimeter = 'commercial' | 'fedramp-moderate' | 'fedramp-high';

// FedRAMP/commercial perimeter is still derived from the hostname (assumed correct).
// Only Isolated Cloud detection is delegated to `@atlaskit/atlassian-context`.
const perimeter: Record<Env, Perimeter> = {
  local: 'commercial',
  dev: 'commercial',
  stg: 'commercial',
  'stg-fedm': 'fedramp-moderate',
  prod: 'commercial',
  'prod-fedm': 'fedramp-moderate',
  'prod-ic': 'commercial',
  unknown: 'commercial',
};

const config: Record<EnvType, EnvConfig> = {
  dev: {
    recaptcha: {
      smsEnrollmentRecaptchaEnterpriseInvisibleKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // no captcha site key
      smsEnrollmentRecaptchaEnterpriseNewKey: '6Ld_RrgpAAAAAC6NtADmI_b9uTwxyDieSQbL0A4O',
    },
  },
  staging: {
    recaptcha: {
      smsEnrollmentRecaptchaEnterpriseInvisibleKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // no captcha site key
      smsEnrollmentRecaptchaEnterpriseNewKey: '6Ld_RrgpAAAAAC6NtADmI_b9uTwxyDieSQbL0A4O',
    },
  },
  prod: {
    recaptcha: {
      smsEnrollmentRecaptchaEnterpriseInvisibleKey: '6LeLRz0kAAAAAPIWGnreLcE0BJaZ17REyxkFhM0E',
      smsEnrollmentRecaptchaEnterpriseNewKey: '6LcjUrgpAAAAANvg2hgPsYsHNO9F21kQVCQwUPoF',
    },
  },
  unknown: {
    recaptcha: {
      smsEnrollmentRecaptchaEnterpriseInvisibleKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // no captcha site key
      smsEnrollmentRecaptchaEnterpriseNewKey: '6Ld_RrgpAAAAAC6NtADmI_b9uTwxyDieSQbL0A4O',
    },
  },
};

export const getCurrentPerimeter = (): Perimeter => perimeter[getCurrentEnv()];

export const isFedM = () => atlassianCloudType[getCurrentEnv()] === 'fedramp-moderate';

/**
 * @deprecated Use `isIsolatedCloud` from `@atlaskit/atlassian-context` directly instead.
 * This wrapper delegates to the supported context library, which resolves Isolated Cloud
 * from the `Atl-Ctx-*` cookies (browser) rather than the hostname. It is retained
 * temporarily so existing callers and mocks keep working during the migration and will be
 * removed once callers are migrated.
 */
export const isIsolatedCloud = () => isIsolatedCloudFromContext();

/**
 * Isolated Cloud detection is delegated to `@atlaskit/atlassian-context` (cookie-based);
 * FedRAMP/commercial continue to be inferred from the hostname.
 */
export const getCurrentCloudType = (): AtlassianCloudType => {
  if (isIsolatedCloud()) {
    return 'isolated-cloud';
  }
  if (isFedM()) {
    return 'fedramp-moderate';
  }
  return 'commercial';
};

export const getConfig = () => config[getCurrentEnvType()];
export const isResetPasswordEnabled = () => {
  if (isIsolatedCloud()) {
    return false;
  }

  // FedRAMP: staging-first rollout — enabled in stg-fedm only, still disabled in prod-fedm.
  if (isFedM()) {
    return getCurrentEnv() === 'stg-fedm';
  }

  // Commercial (local/dev/stg/prod).
  return true;
};

/**
 * PBR mounts ID frontend below `/id`, while directly hosted ID frontend has no path prefix.
 * This is intentionally based on the mount path, not the hostname: tenant domains have no
 * stable naming convention.
 */
export const getIdPathPrefix = (pathname: string): '' | '/id' =>
  pathname === '/id' || pathname.startsWith('/id/') ? '/id' : '';

/**
 * Path based aware URL builder for ID backend endpoints. The backendPath is appended to the mount path prefix, which is determined by the pathname.
 * @param origin
 * @param pathname
 * @param backendPath
 * @returns
 */
export const getIdacBackendUrl = (
  origin: string,
  pathname: string,
  backendPath: string
): string => {
  // Not using URL constructor to retain original behaviour of not doing any encoding of the input strings.
  // This is important for the backendPath, which may contain query parameters that are already encoded.
  const path = `${getIdPathPrefix(pathname)}${backendPath}`;
  return `${origin}${path || '/'}`;
};

/** Builds an ID backend URL using the current browser location. */
export const getCurrentIdacBackendUrl = (backendPath: string): string =>
  getIdacBackendUrl(window.location.origin, window.location.pathname, backendPath);

/**
 * Uses the current ID mount when PBR is enabled. `legacyPath` preserves the exact
 * gate-disabled URL for callers that previously used an absolute URL.
 * Callers with an explicit location can supply its origin and pathname.
 */
export const getGatedIdacBackendUrl = (
  backendPath: string,
  {
    legacyPath = backendPath,
    pbrEnabled = checkFeatureGate(AidFrontendFeatureGate.PBR_ENABLED_API_ENDPOINT_PATH_CALCULATION),
    location = window.location,
  }: {
    legacyPath?: string;
    pbrEnabled?: boolean;
    location?: Pick<Location, 'origin' | 'pathname'>;
  } = {}
): string =>
  pbrEnabled ? getIdacBackendUrl(location.origin, location.pathname, backendPath) : legacyPath;
